import React, {useEffect, useMemo } from "react";

import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import * as constants from "../../constants/authConstants";
import * as routeConstants from "../../constants/routeConstants";

import { login, logout } from "../../store/auth/auth-actions";
import { getMyProfile } from "../../store/auth/auth-actions";

import {getAbortSignal} from '../../util/signalHelper';


const AUTH_POPUP_WINDOW_NAME = "AUTH_POPUP";
const TOKEN_REFRESH_INTERVAL_IN_MILLIS = process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS;// ? process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS : 10*60*60*1000;
const popupEnabled = process.env.REACT_APP_AUTH_POPUP_ENABLED === 'true';
const isPopup = window.name === AUTH_POPUP_WINDOW_NAME;

//make sure you understand everything properly before editing this file
const Authentication = props => {

  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const isAuthenticating = location.pathname === routeConstants.ROUTE_AUTH;
  const isLogginOut = location.pathname === routeConstants.ROUTE_LOGOUT;
  
  let token = useMemo(() => auth.token, [auth.token]);
  let expiresAt = useMemo(() => auth.expiresAt ? new Date(auth.expiresAt) : null, [auth.expiresAt]);
  let callLogin = false;

  //const isExpired = expiresAt && !(expiresAt.getTime() - new Date().getTime());
  const isExpired = expiresAt && (expiresAt.getTime() - new Date().getTime()) < 0;


  console.log(`Authentication -> isAuthenticating=${isAuthenticating}, isLogginOut=${isLogginOut}, token set=${!!token}, isExpired=${isExpired}, popupEnabled=${popupEnabled}, isPopup=${isPopup}, windowName=${window.name}, isLoggedIn=${auth.isLoggedIn}`)

  //sets the popup 
  useEffect(() => {
    console.log("Authentication -> useEffect popup")
    const popupLoginHandler = (token, expiresAt) => {
        console.log("Authentication -> URL popup handler called")
        dispatch(login(token, expiresAt));
    }

    window.handlePopup = popupLoginHandler;
  }, []);

  useEffect(() => {
    if (!isLogginOut) return;
    if (!token) return;
    dispatch(logout());
  }, [isLogginOut])

  //effect that sets the token or redirects to the auth page
  useEffect(() => {
    console.log("Authentication -> useEffect refresh token or redirect login")


    console.count("Authentication -> useEffect login");
    if (!callLogin) return;
    if (isExpired) return;
    if (!token) return;
    if (isLogginOut) return;
    console.count("Authentication -> useEffect login: dispatching");
    
    //About to expire -> refresh token to avoid token expires during session 
    //Example: token valid for 24h. User comes in on day one at 9:00. So token gets created with expire the next day at 9:00. 
    //Second day user comes in again, but earlier on the day that the first day: 8:55. Token still OK and expiration not refreshed. 
    //5 minutes later: user get's thrown out when trying to do something because token expired.
    //Proposal: token valid for 48h (=max) - refresh when accessing the '/auth' url(user clicks on admin->skillmatrix) and token validity less then 24h.
    //User is just coming in to the application so isn't really doing anything yet. Token get's refreshed so user can work for the day.
    console.log("Authentication -> Time to expire: ", (expiresAt.getTime() - new Date().getTime()), " Refresh when less then: ", TOKEN_REFRESH_INTERVAL_IN_MILLIS);
    
    const doTokenRefresh = TOKEN_REFRESH_INTERVAL_IN_MILLIS && expiresAt.getTime() - new Date().getTime() <= TOKEN_REFRESH_INTERVAL_IN_MILLIS;
    
    if(doTokenRefresh) {
      console.log("Authentication -> Token almost expiring - Better to refresh token now");
      
      if (popupEnabled && !isPopup) {
        //open popup
        window.open(`${constants.LOGIN_URL}`, AUTH_POPUP_WINDOW_NAME, "'width=600,height=400,resizable=yes,scrollbars=yes'");
        return;
      }
      
      window.location.href = constants.LOGIN_URL;
      return;
    }

    if (isPopup) {
      //call parent function (useEffect executes after the component is loaded, token is set below)
      window.opener.handlePopup(token, expiresAt);
      window.close();
      return;
    }

    if (!isLogginOut) dispatch(login(token, expiresAt));

  }, [dispatch, token, expiresAt, callLogin, isExpired]);

  //effect that gets the user profile
  useEffect(() => {
    const [signal, controller] = getAbortSignal();

    console.count("Authentication -> useEffect profile");
    if (!auth.token) return;
    if (isExpired) return;
    
    console.count("Authentication -> useEffect profile: getMyProfile");
    dispatch(getMyProfile(auth.token, signal));

    //cleanup functions appear to be a necessity nowadays for future react versions regarding improved performance called "reusable state"
    //https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state
    return () => {
      console.count("Authentication -> cancel getMyProfile");
      controller.abort();
    }
  }, [dispatch, auth.token, isExpired]);

  //effect that does the logout after the token has expired
  useEffect(() => {
    console.log("Authentication -> useEffect expired");
    if (!isExpired) return;
    console.count("Authentication -> useEffect expired: logout");
    dispatch(logout());
  }, [dispatch, isExpired]);

  //redirect to location when login was successful
  useEffect(() => {
    console.log("Authentication -> get token from localstorage")

    const redirectToLocation = window.localStorage.getItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);

    if (redirectToLocation && auth.isLoggedIn && !isAuthenticating) {
      window.localStorage.removeItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);
      console.log(`Authentication -> useEffect: redirecting to: ${redirectToLocation}`);
      navigate(redirectToLocation);
    } 
  }, [auth.isLoggedIn, isAuthenticating, navigate]);

  if (auth.doRedirectLogout) {
    console.log("Authentication -> directing to logout url");
    window.location.href = constants.LOGOUT_URL;
    return (<></>);
  }

  //default behaviour
  if (auth.isLoggedIn && !isAuthenticating) {    
    //load content, useEffect will handle redirect if set
    //console.log("Authentication -> user is logged in, route is not authenticating");
    return (props.children);
  }

  console.log("Authentication -> user is not yet authenticated")

  //Check for token and expiresIn in url parameters
  if (window.location.href.includes(constants.KEY_ACCESS_TOKEN_PREFIXED)) {
    console.log("Authentication -> get token from url")
    //get token from url
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    token = urlParams.get(constants.KEY_ACCESS_TOKEN);

    //get expiredIn from url
    if (window.location.href.includes(constants.KEY_EXPIRES_IN)) {
      const expiresIn = urlParams.get(constants.KEY_EXPIRES_IN);
      expiresAt = new Date(new Date().getTime() + (1000 * expiresIn));
    }
  }

  //if there is no token and the path is /auth -> get token (=redirect to login url at Genesys)
  if (!token && isAuthenticating) {

    if (popupEnabled && !isPopup) {
      console.log("Authentication -> will open login popup");
      window.open(`${constants.LOGIN_URL}`, AUTH_POPUP_WINDOW_NAME, "'width=600,height=400,resizable=yes,scrollbars=yes'");
    } else {
      console.log("Authentication -> will direct to login url");
      window.location.href = constants.LOGIN_URL;
    }

    return (<></>);
  }

/*
console.log("Token: ", token);
console.log("now: ", new Date().getTime());
console.log("expiresAt: ", expiresAt.getTime());
console.log("diff: ", (expiresAt.getTime() - new Date().getTime()));
console.log("false-y: ", !(expiresAt.getTime() - new Date().getTime()));
console.log("false-y 2: ", (expiresAt.getTime() - new Date().getTime()) < 0);
console.log("isExpired: ", isExpired);
console.log("isAuthenticating: ", isAuthenticating);
*/

  //if there is an expired token and the path is /auth -> get token (=redirect to login url at Genesys)
  if (token && isExpired && isAuthenticating) {

    if (popupEnabled && !isPopup) {
      console.log("Authentication -> expired token - will open login popup");
      window.open(`${constants.LOGIN_URL}`, AUTH_POPUP_WINDOW_NAME, "'width=600,height=400,resizable=yes,scrollbars=yes'");
    } else {
      console.log("Authentication -> expired token - will direct to login url");
      window.location.href = constants.LOGIN_URL;
    }

    return (<></>);
  }

  //not being logged in will trigger log in in useEffect -> set redirect page
  if (!auth.isLoggedIn && !isAuthenticating) {
    console.log("Authentication -> set redirect after login")
    window.localStorage.setItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN, location.pathname);
  }

  //make sure the useEffect logs you in after page render
  callLogin = true;

  //if you arrive here this means that the token is set.
  //The login useEffect will also be called
  //It is necessary to move away from the /auth endpoint -> go home
  if (isAuthenticating) {
    console.log("Authentication -> token is set, page is authenticating, move to default route");
    return (<Navigate replace to={routeConstants.ROUTE_DEFAULT} />);
  }
  
  //load children -> when logged out and no token is set in the local storage
  console.log("Authentication -> no token is set, user is logged out");
  return (props.children);
}

export default Authentication;